import React, { useEffect, useCallback, useRef } from 'react';
import useSettingsStore from '../store/settingsStore';
import { Layout, Menu, Avatar, Dropdown, Button, theme, Badge, Popover, List, Tooltip, Grid, Space } from 'antd';
import {
  DashboardOutlined,
  TeamOutlined,
  DollarOutlined,
  BellOutlined,
  TrophyOutlined,
  LogoutOutlined,
  UserOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useState } from 'react';
import dayjs from 'dayjs';
import useAuthStore from '../store/authStore';
import { logout } from '../api/auth';
import api from '../api/axios';
import { getNotifications, getUnreadCount, markAsRead, markAllAsRead } from '../api/notifications';
import KvkkConsentModal from '../components/common/KvkkConsentModal';
import ChatWidget from '../components/chat/ChatWidget';
import { playNotificationSound } from '../utils/notificationSound';
import { requestNotificationPermission, showBrowserNotifications } from '../utils/browserNotifications';
import BildirimIzniDugmesi from '../components/common/BildirimIzniDugmesi';
import UygulamayiYukleDugmesi from '../components/common/UygulamayiYukleDugmesi';
import { notificationTarget } from '../utils/notificationTarget';

const { Header, Sider, Content } = Layout;

const ParentLayout = () => {
  const { appName, logoUrl, vapidPublicKey, fetchSettings } = useSettingsStore();
  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => { requestNotificationPermission(); }, []);
  const [collapsed, setCollapsed] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout: storeLogout, isImpersonating, impersonationId, originalUser, stopImpersonation } = useAuthStore();
  const { token } = theme.useToken();
  // Telefonda bildirim penceresinin hizalaması ve genişliği değişiyor
  // (aşağıdaki Popover'a bak) — geniş ekranda hiçbir şey değişmiyor.
  const ekran = Grid.useBreakpoint();
  const genisEkran = !!ekran.md;
  const prevUnreadCountRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await getUnreadCount();
      const newCount = res.data.unread_count || 0;
      if (prevUnreadCountRef.current !== null && newCount > prevUnreadCountRef.current) {
        playNotificationSound();
        const delta = newCount - prevUnreadCountRef.current;
        try {
          const latest = await getNotifications({ per_page: Math.min(delta, 5) });
          showBrowserNotifications(latest.data.data || [], logoUrl);
        } catch {}
      }
      prevUnreadCountRef.current = newCount;
      setUnreadCount(newCount);
    } catch {}
  }, [logoUrl]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleNotifOpen = async (open) => {
    setNotifOpen(open);
    if (open) {
      try {
        const res = await getNotifications({ per_page: 5 });
        setNotifications(res.data.data || []);
        await fetchUnreadCount();
      } catch {}
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await markAsRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setUnreadCount(0);
    } catch {}
  };

  const handleStopImpersonation = async () => {
    try {
      const res = await api.delete(`/admin/impersonate/${impersonationId}`);
      stopImpersonation(res.data.user);
    } catch {
      stopImpersonation(originalUser);
    }
    navigate('/users');
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch {}
    finally {
      storeLogout();
      navigate('/login');
    }
  };

  const menuItems = [
    { key: '/parent/dashboard', icon: <DashboardOutlined />, label: 'Genel Bakış' },
    { key: '/parent/students', icon: <TeamOutlined />, label: 'Öğrencilerim' },
    { key: '/parent/payments', icon: <DollarOutlined />, label: 'Ödemeler' },
    { key: '/parent/announcements', icon: <BellOutlined />, label: 'Duyurular' },
    // Yarışmalar her veliye görünür; katılım daveti olmayan velide sayfa
    // "katılabileceğiniz bir yarışma yok" der. Menüyü koşullu göstermek için
    // her açılışta ekstra bir istek atmak gerekirdi.
    { key: '/parent/competitions', icon: <TrophyOutlined />, label: 'Yarışmalar' },
  ];

  const userMenuItems = [
    { key: 'profile', icon: <UserOutlined />, label: 'Profil', onClick: () => navigate('/parent/profile') },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Çıkış Yap', onClick: handleLogout, danger: true },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <KvkkConsentModal />
      <ChatWidget />
      {/*
        MOBİLDE MENÜ TAMAMEN GİZLENİR (2026-08-26).

        Önceden `breakpoint` yoktu: telefonda menü kalıcı olarak 80px yer
        kaplıyordu — 375px'lik bir ekranın %21'i. Veli uygulaması henüz
        herkeste olmadığı için veliler web arayüzünü telefondan açıyor.

        `collapsedWidth={0}` + `breakpoint="lg"`: 992px altında menü
        kendiliğinden kapanıp GENİŞLİK BIRAKMIYOR; başlıktaki mevcut düğme
        aç/kapa olarak çalışıyor. Masaüstünde davranış değişmiyor.
      */}
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        breakpoint="lg"
        collapsedWidth={0}
        onBreakpoint={(dar) => setCollapsed(dar)}
        style={{ background: token.colorBgContainer, borderRight: `1px solid ${token.colorBorderSecondary}` }}
      >
        <div className="flex items-center justify-center p-4 border-b">
          {!collapsed
            ? <span className="font-bold text-lg text-blue-600">{appName}</span>
            : <span className="font-bold text-lg text-blue-600">{appName.split(' ').map(w => w[0]).join('').toUpperCase()}</span>
          }
        </div>
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={({ key }) => {
            navigate(key);
            // Telefonda menü açıkken sayfayı itiyor; seçim yapıldıktan
            // sonra açık bırakmanın anlamı yok, kendiliğinden kapansın.
            if (window.innerWidth < 992) setCollapsed(true);
          }}
          style={{ borderRight: 0 }}
        />
      </Sider>

      {/*
        `minWidth: 0` ŞART — kaldırılırsa mobilde sayfa yana kayar.

        Bu Layout, Sider'ın yanındaki bir flex çocuğu. Flex çocukları
        varsayılan `min-width: auto` ile gelir, yani İÇERİĞİNDEN DAHA DAR
        OLMAYI REDDEDER: içeride geniş bir şey (uzun sekme çubuğu, tablo)
        olduğunda kendisi genişliyor ve sayfayı itiyordu.

        Ölçüldü (2026-08-26, 375px): Profil sayfası 421px, Öğrencilerim >
        Devam Durumu 591px genişliğindeydi. Tek tek sayfaları yamamak yerine
        kaynağı burada kesiyoruz.
      */}
      <Layout style={{ minWidth: 0 }}>
        <Header
          style={{
            padding: '0 16px',
            background: token.colorBgContainer,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
          />

          <div className="flex items-center gap-4">
            {/*
              BİLDİRİM PENCERESİ — telefonda "bottom", masaüstünde "bottomRight".

              `bottomRight` pencerenin SAĞ kenarını zile hizalıyor ve pencere
              oradan sola doğru açılıyor. Telefonda zil sağ kenarda değil
              ~219px'te (yanındaki kullanıcı adı onu sola itiyor); 340px'lik
              pencere sola açılınca sol kenarı -145px'e düşüyor ve neredeyse
              yarısı ekran dışında kalıyordu — kullanıcının "bildirimlere
              tıklayınca sayfa yarım geliyor" dediği şey bu (2026-08-26).

              `bottom` pencereyi zilin ortasına hizalıyor, ekrana sığıyor.
            */}
            <Popover
              trigger="click"
              open={notifOpen}
              onOpenChange={handleNotifOpen}
              placement={genisEkran ? 'bottomRight' : 'bottom'}
              title={
                <div className="flex justify-between items-center" style={{ minWidth: genisEkran ? 300 : 0 }}>
                  <span>Bildirimler</span>
                  {unreadCount > 0 && (
                    <Button size="small" type="link" icon={<CheckOutlined />} onClick={handleMarkAllAsRead}>
                      Tümünü Okundu İşaretle
                    </Button>
                  )}
                </div>
              }
              content={
                /*
                  Sabit 340px telefonda taşıyordu. 48px pay bırakılıyor:
                  24'ü Popover'ın kendi iç dolgusu (içerik kutusunun ÜSTÜNE
                  binen ~12px×2), kalanı kenar boşluğu. 24 ile denendi,
                  pencere hâlâ 4px dışarı taşıyordu.
                */
                <div style={{ width: genisEkran ? 340 : 'min(340px, calc(100vw - 48px))' }}>
                  {notifications.length === 0 ? (
                    <p className="text-gray-400 text-center py-4">Bildirim yok.</p>
                  ) : (
                    <List
                      dataSource={notifications}
                      renderItem={(n) => (
                        <List.Item
                          style={{ background: n.read_at ? 'transparent' : '#f0f7ff', cursor: notificationTarget(n, user?.role) ? 'pointer' : 'default', padding: '8px 12px', borderRadius: 4 }}
                          onClick={() => {
                            if (!n.read_at) handleMarkAsRead(n.id);
                            const target = notificationTarget(n, user?.role);
                            if (target) { setNotifOpen(false); navigate(target); }
                          }}
                          actions={[
                            !n.read_at && (
                              <Tooltip title="Okundu işaretle" key="read">
                                <CheckOutlined
                                  style={{ color: '#1677ff' }}
                                  onClick={(e) => { e.stopPropagation(); handleMarkAsRead(n.id); }}
                                />
                              </Tooltip>
                            ),
                          ].filter(Boolean)}
                        >
                          <List.Item.Meta
                            title={<span style={{ fontWeight: n.read_at ? 400 : 600, fontSize: 13 }}>{n.title}</span>}
                            description={
                              <div>
                                <div style={{ fontSize: 12, color: '#555' }}>{n.body}</div>
                                <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
                                  {dayjs(n.created_at).format('DD.MM.YYYY HH:mm')}
                                </div>
                              </div>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  )}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 8, borderTop: '1px solid #f0f0f0', paddingTop: 8, marginTop: 4,
                  }}>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => { setNotifOpen(false); navigate('/parent/profile?tab=notifications'); }}
                    >
                      Tümünü Gör
                    </Button>
                    {/* Bildirim ayarı aranacak yer burası. Başlıkta sürekli
                        durması masaüstünde kötü görünüyordu (Eren Bey,
                        22.09.2026); buradan hem her cihazda ulaşılabiliyor
                        hem de göz tırmalamıyor.

                        Yükleme düğmesi de yanında: veli Chrome'un ⋮
                        menüsündeki "Ana ekrana ekle"yi bulamıyordu
                        (23.09.2026). Yalnızca yüklenebilir durumda
                        çiziliyor, kuruluysa kendiliğinden kayboluyor. */}
                    <Space size={4}>
                      <UygulamayiYukleDugmesi />
                      <BildirimIzniDugmesi vapidPublicKey={vapidPublicKey} rol={user?.role} gorunum="link" />
                    </Space>
                  </div>
                </div>
              }
            >
              <Badge count={unreadCount} size="small">
                <Button type="text" icon={<BellOutlined />} shape="circle" />
              </Badge>
            </Popover>

            {/* Başlıkta YALNIZCA TELEFONDA ve YALNIZCA HENÜZ ABONE
                DEĞİLSE. Velinin bunu bulabilmesi gerekiyor — ~530 kişiye
                anlatılacak bir özellik, yalnızca menü içinde kalırsa
                çoğu hiç fark etmez. Abone olunca kayboluyor, yani yeşil
                tik başlıkta asılı kalmıyor. Masaüstünde hiç çizilmiyor;
                oradaki yeri bildirim menüsünün altı. */}
            {!genisEkran && (
              <BildirimIzniDugmesi vapidPublicKey={vapidPublicKey} rol={user?.role} aboneyseGizle />
            )}

            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <div className="flex items-center gap-2 cursor-pointer">
                <Avatar icon={<UserOutlined />} />
                <span>{user?.name} {user?.surname}</span>
              </div>
            </Dropdown>
          </div>
        </Header>

        {isImpersonating && (
          <div style={{ background: '#fff3cd', borderBottom: '1px solid #ffc107', padding: '8px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ color: '#856404', fontWeight: 500 }}>
              Taklit modu: <strong>{user?.name} {user?.surname}</strong> olarak görüntülüyorsunuz.
              Gerçek hesap: {originalUser?.name} {originalUser?.surname}
            </span>
            <Button size="small" danger onClick={handleStopImpersonation}>Takliti Sonlandır</Button>
          </div>
        )}

        <Content
          style={{
            /*
              TELEFONDA DAR BOŞLUK (2026-08-26).

              Masaüstü değerleri (24px kenar + 24px dolgu) 375px'lik bir
              ekranın 80px'ini yiyordu — %21'i. Sekme çubuğuna ancak 268px
              kalıyor, beş sekmeden biri buçuğu görünüyordu. Telefonda
              8px/12px ile ~56px geri kazanılıyor.
            */
            margin: genisEkran ? '24px 16px' : '8px',
            padding: genisEkran ? 24 : 12,
            background: token.colorBgContainer,
            borderRadius: token.borderRadiusLG,
            minHeight: 280,
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default ParentLayout;
